import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  CheckCircle,
  DateRange,
  Devices,
  Error,
  HourglassEmpty,
  Payment,
  Person,
  Queue,
  Receipt,
  Search,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import AppliedFiltersBar from "../../components/AppliedFiltersBar";
import FilterButton from "../../components/FilterButton";
import FilterPopover from "../../components/FilterPopover";
import MetricsStrip from "../../components/MetricsStrip";

import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((item) => item.id === invoice.id);

      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICE") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((item) => item.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    }

    return [invoice, ...state];
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;
    const invoiceIndex = state.findIndex((item) => item.id === invoiceId);

    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  filterChips: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  tableSearch: {
    width: 300,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  tableCellStrong: {
    fontWeight: 600,
  },
  dueDateCell: {
    display: "flex",
    flexDirection: "column",
  },
  helperText: {
    opacity: 0.72,
  },
  chipPaid: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    fontWeight: 700,
  },
  chipPending: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    fontWeight: 700,
  },
  chipOverdue: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontWeight: 700,
  },
  paymentButton: {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 999,
  },
  paidButton: {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 999,
  },
  overdueRow: {
    backgroundColor: "rgba(255, 188, 188, 0.15)",
  },
  mobileView: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  desktopView: {
    display: "block",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  mobileSection: {
    padding: theme.spacing(1.5),
  },
  mobileGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.5),
  },
  invoiceCard: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  invoiceCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  invoiceCardTitle: {
    fontWeight: 600,
  },
  invoiceCardMeta: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  cardStatusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  invoiceDetailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  detailItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  detailIcon: {
    fontSize: 18,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  invoiceAmount: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    opacity: 0.72,
  },
  loadMoreRow: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6, 0),
  },
}));

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

const Invoices = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [companyPlan, setCompanyPlan] = useState(null);

  const handleOpenContactModal = (invoice) => {
    const invoiceWithPlanValue = {
      ...invoice,
      value:
        companyPlan && companyPlan.amount ? parseFloat(companyPlan.amount) : invoice.value,
    };

    setStoragePlans(invoiceWithPlanValue);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const fetchCompanyPlan = useCallback(async () => {
    try {
      if (!user || !user.companyId) return;

      const companyResponse = await api.get(`/companies/${user.companyId}`);
      const company = companyResponse.data;

      if (company && company.planId) {
        const planResponse = await api.get(`/plans/${company.planId}`);
        setCompanyPlan(planResponse.data);
      }
    } catch (err) {
      toastError(err);
    }
  }, [user]);

  const fetchInvoices = useCallback(async (currentPage = 1, currentSearch = "") => {
    setLoading(true);

    try {
      const { data } = await api.get("/invoices/all", {
        params: { searchParam: currentSearch, pageNumber: currentPage },
      });

      dispatch({ type: "LOAD_INVOICES", payload: data.invoices || [] });
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanyPlan();
  }, [fetchCompanyPlan]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchInvoices(pageNumber, searchParam);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchInvoices, pageNumber, searchParam]);

  const getDaysToDueDate = useCallback((record) => {
    const hoje = moment().startOf("day");
    const vencimento = moment(record.dueDate).startOf("day");

    return vencimento.diff(hoje, "days");
  }, []);

  const getInvoiceStatusKey = useCallback(
    (record) => {
      if (record.status === "paid") return "paid";
      return getDaysToDueDate(record) < 0 ? "overdue" : "open";
    },
    [getDaysToDueDate]
  );

  const getInvoiceStatus = useCallback(
    (record) => {
      const statusKey = getInvoiceStatusKey(record);

      if (statusKey === "paid") {
        return {
          text: "Pago",
          chipClass: classes.chipPaid,
          icon: <CheckCircle fontSize="small" />,
        };
      }

      if (statusKey === "overdue") {
        return {
          text: "Vencido",
          chipClass: classes.chipOverdue,
          icon: <Error fontSize="small" />,
        };
      }

      return {
        text: "Em aberto",
        chipClass: classes.chipPending,
        icon: <HourglassEmpty fontSize="small" />,
      };
    },
    [classes.chipOverdue, classes.chipPaid, classes.chipPending, getInvoiceStatusKey]
  );

  const renderDaysLeft = useCallback(
    (record) => {
      if (record.status === "paid") {
        return "Liquidada";
      }

      const daysToDueDate = getDaysToDueDate(record);

      if (daysToDueDate < 0) {
        return `Vencido ha ${Math.abs(daysToDueDate)} dias`;
      }

      if (daysToDueDate === 0) {
        return "Vence hoje";
      }

      return `Vence em ${daysToDueDate} dias`;
    },
    [getDaysToDueDate]
  );

  const visibleInvoices = useMemo(
    () =>
      invoices.filter((invoice) => {
        if (statusFilter === "all") return true;
        return getInvoiceStatusKey(invoice) === statusFilter;
      }),
    [getInvoiceStatusKey, invoices, statusFilter]
  );

  const summary = useMemo(() => {
    const nextSummary = {
      visible: visibleInvoices.length,
      open: 0,
      paid: 0,
      overdue: 0,
    };

    visibleInvoices.forEach((invoice) => {
      const statusKey = getInvoiceStatusKey(invoice);

      if (statusKey === "paid") nextSummary.paid += 1;
      if (statusKey === "open") nextSummary.open += 1;
      if (statusKey === "overdue") nextSummary.overdue += 1;
    });

    return nextSummary;
  }, [getInvoiceStatusKey, visibleInvoices]);

  const summaryCards = useMemo(
    () => [
      { key: "invoices", label: "Faturas", value: summary.visible },
      { key: "open", label: "Em aberto", value: summary.open },
      { key: "paid", label: "Pagas", value: summary.paid },
      { key: "overdue", label: "Vencidas", value: summary.overdue },
    ],
    [summary]
  );

  const statusLabels = {
    open: "Em aberto",
    paid: "Pagas",
    overdue: "Vencidas",
  };
  const appliedFilters =
    statusFilter === "all"
      ? []
      : [
          {
            key: "status",
            label: `Status: ${statusLabels[statusFilter]}`,
            onRemove: () => setStatusFilter("all"),
          },
        ];
  const planAmount = companyPlan?.amount ? formatCurrency(companyPlan.amount) : null;

  const handleSearch = (event) => {
    dispatch({ type: "RESET" });
    setSearchParam(event.target.value);
    setPageNumber(1);
  };

  const handleRefresh = async () => {
    dispatch({ type: "RESET" });

    if (pageNumber !== 1) {
      setPageNumber(1);
      return;
    }

    await fetchInvoices(1, searchParam);
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderInvoiceAction = (invoice) => {
    const statusInfo = getInvoiceStatus(invoice);

    if (statusInfo.text !== "Pago") {
      return (
        <Button
          size="small"
          variant="contained"
          color="primary"
          className={classes.paymentButton}
          startIcon={<Payment />}
          onClick={() => handleOpenContactModal(invoice)}
        >
          Pagar
        </Button>
      );
    }

    return (
      <Button
        size="small"
        variant="outlined"
        color="primary"
        className={classes.paidButton}
        startIcon={<CheckCircle />}
        disabled
      >
        Pago
      </Button>
    );
  };

  const renderMobileCards = () => {
    if (loading && visibleInvoices.length === 0) {
      return (
        <Box className={classes.loadingBox}>
          <CircularProgress />
        </Box>
      );
    }

    if (!loading && visibleInvoices.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body2">Nenhuma fatura encontrada.</Typography>
        </Box>
      );
    }

    return (
      <div className={classes.mobileGrid}>
        {visibleInvoices.map((invoice) => {
          const statusInfo = getInvoiceStatus(invoice);

          return (
            <Card key={invoice.id} className={classes.invoiceCard}>
              <CardContent>
                <Box className={classes.invoiceCardHeader}>
                  <Box>
                    <Typography variant="h6" className={classes.invoiceCardTitle}>
                      {companyPlan?.name || invoice.detail}
                    </Typography>
                    <Typography variant="body2" className={classes.invoiceCardMeta}>
                      {invoice.detail || `Fatura ${invoice.id}`}
                    </Typography>
                  </Box>
                  <Receipt color="primary" />
                </Box>

                <Box className={classes.cardStatusRow}>
                  <Chip
                    icon={statusInfo.icon}
                    label={statusInfo.text}
                    className={statusInfo.chipClass}
                    size="small"
                  />
                  <Typography variant="body2" className={classes.helperText}>
                    {renderDaysLeft(invoice)}
                  </Typography>
                </Box>

                <div className={classes.invoiceDetailGrid}>
                  <Box className={classes.detailItem}>
                    <Person className={classes.detailIcon} />
                    <Typography variant="body2">{companyPlan?.users || 0} usuarios</Typography>
                  </Box>
                  <Box className={classes.detailItem}>
                    <Devices className={classes.detailIcon} />
                    <Typography variant="body2">
                      {companyPlan?.connections || 0} conexoes
                    </Typography>
                  </Box>
                  <Box className={classes.detailItem}>
                    <Queue className={classes.detailIcon} />
                    <Typography variant="body2">{companyPlan?.queues || 0} filas</Typography>
                  </Box>
                  <Box className={classes.detailItem}>
                    <DateRange className={classes.detailIcon} />
                    <Typography variant="body2">
                      {moment(invoice.dueDate).format("DD/MM/YYYY")}
                    </Typography>
                  </Box>
                </div>

                <Typography variant="h6" className={classes.invoiceAmount}>
                  {planAmount || formatCurrency(invoice.value)}
                </Typography>

                <Box className={classes.cardActions}>{renderInvoiceAction(invoice)}</Box>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  const isLoadingFallback = !user || !user.companyId || !companyPlan;

  if (isLoadingFallback) {
    return (
      <MainContainer className={classes.mainContainer} fullWidth>
        <div className={classes.contentStack}>
          <Box className={classes.hero}>
            <Typography variant="h5" className={classes.heroTitle}>
              Financeiro
            </Typography>
          </Box>
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}
      />

      <FilterPopover
        id="finance-filters"
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        description="Selecione o status das faturas que deseja consultar."
        onClose={() => setFilterAnchorEl(null)}
        onClear={handleClearFilters}
        clearDisabled={appliedFilters.length === 0}
        onApply={() => setFilterAnchorEl(null)}
      >
        <Typography variant="body2" color="textSecondary">
          Status
        </Typography>
        <Box className={classes.filterChips}>
          <Chip
            label="Todas"
            clickable
            color={statusFilter === "all" ? "primary" : "default"}
            variant={statusFilter === "all" ? "default" : "outlined"}
            onClick={() => setStatusFilter("all")}
          />
          <Chip
            label="Em aberto"
            clickable
            color={statusFilter === "open" ? "primary" : "default"}
            variant={statusFilter === "open" ? "default" : "outlined"}
            onClick={() => setStatusFilter("open")}
          />
          <Chip
            label="Pagas"
            clickable
            color={statusFilter === "paid" ? "primary" : "default"}
            variant={statusFilter === "paid" ? "default" : "outlined"}
            onClick={() => setStatusFilter("paid")}
          />
          <Chip
            label="Vencidas"
            clickable
            color={statusFilter === "overdue" ? "primary" : "default"}
            variant={statusFilter === "overdue" ? "default" : "outlined"}
            onClick={() => setStatusFilter("overdue")}
          />
        </Box>
      </FilterPopover>

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              Financeiro
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button variant="outlined" color="primary" onClick={handleRefresh} disabled={loading}>
              Atualizar
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores financeiros" items={summaryCards} />

        <AppliedFiltersBar
          filters={appliedFilters}
          onClearAll={handleClearFilters}
        />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Faturas operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder="Buscar por descricao ou vencimento"
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar faturas" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <FilterButton
                count={appliedFilters.length}
                open={Boolean(filterAnchorEl)}
                controls="finance-filters"
                onClick={(event) => setFilterAnchorEl(event.currentTarget)}
              />
              <Chip size="small" label={companyPlan?.name || "Plano"} />
              {planAmount ? <Chip size="small" label={planAmount} /> : null}
            </Box>
          </Box>

          <div className={classes.mobileView}>
            <div className={classes.mobileSection}>{renderMobileCards()}</div>
          </div>

          <div className={classes.desktopView}>
            <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>Plano</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Usuarios
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Conexoes
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Filas
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Valor
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Vencimento
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Status
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Acao
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleInvoices.map((invoice) => {
                    const statusInfo = getInvoiceStatus(invoice);
                    const isOverdue = getInvoiceStatusKey(invoice) === "overdue";

                    return (
                      <TableRow
                        key={invoice.id}
                        hover
                        className={isOverdue ? classes.overdueRow : undefined}
                      >
                        <TableCell>
                          <Typography className={classes.tableCellStrong}>
                            {companyPlan?.name || invoice.detail}
                          </Typography>
                          <Typography variant="body2" className={classes.helperText}>
                            {invoice.detail || `Fatura ${invoice.id}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{companyPlan?.users || 0}</TableCell>
                        <TableCell align="center">{companyPlan?.connections || 0}</TableCell>
                        <TableCell align="center">{companyPlan?.queues || 0}</TableCell>
                        <TableCell align="center" className={classes.tableCellStrong}>
                          {planAmount || formatCurrency(invoice.value)}
                        </TableCell>
                        <TableCell align="center">
                          <Box className={classes.dueDateCell}>
                            <Typography variant="body2">
                              {moment(invoice.dueDate).format("DD/MM/YYYY")}
                            </Typography>
                            <Typography variant="caption" className={classes.helperText}>
                              {renderDaysLeft(invoice)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={statusInfo.icon}
                            label={statusInfo.text}
                            className={statusInfo.chipClass}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">{renderInvoiceAction(invoice)}</TableCell>
                      </TableRow>
                    );
                  })}

                  {loading ? <TableRowSkeleton columns={8} /> : null}

                  {!loading && visibleInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className={classes.emptyState}>
                        Nenhuma fatura encontrada.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {!loading && hasMore && invoices.length > 0 ? (
            <Box className={classes.loadMoreRow}>
              <Button variant="outlined" color="primary" onClick={loadMore}>
                Carregar mais
              </Button>
            </Box>
          ) : null}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Invoices;
